import React, { useEffect, useRef, useState, useCallback, memo } from "react";
import { IconClose } from "icons";
import { useTheme } from "hooks/useTheme";
import { SnackbarData, SNACKBAR_COLORS, SNACKBAR_ROLES } from "./types";
import clsx from "clsx";

interface SnackbarProps {
  data: SnackbarData;
  onDismiss: (id: string) => void;
  className?: string;
}

const Snackbar = memo(({ data, onDismiss, className = "" }: SnackbarProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<number>(0);
  const remainingTimeRef = useRef<number>(data.duration || 5000);

  const { effectiveTheme } = useTheme();
  const isDark = effectiveTheme === "dark";

  const Icon = data.icon;
  const duration = data.persistent ? 0 : data.duration ?? 5000;

  const getColor = () => {
    const baseColor = SNACKBAR_COLORS[data.type];
    if (!baseColor) return isDark ? "#ffffff" : "#2B2B2B";

    // Success: green in dark mode, purple in light mode
    if (data.type === 0) {
      return isDark ? "#D1EF70" : "#6266F0";
    }

    // Info: light gray in dark mode, dark gray in light mode
    if (data.type === 3) {
      return isDark ? "#E4E4E4" : "#555555";
    }

    return baseColor;
  };

  const color = getColor();
  const role = SNACKBAR_ROLES[data.type] || "status";

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => onDismiss(data.id), 300);
  }, [data.id, onDismiss]);

  const startTimer = useCallback(() => {
    if (duration <= 0) return;

    startTimeRef.current = Date.now();

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      handleDismiss();
    }, remainingTimeRef.current);
  }, [duration, handleDismiss]);

  const pauseTimer = useCallback(() => {
    if (duration <= 0 || !timerRef.current) return;

    clearTimeout(timerRef.current);
    timerRef.current = null;

    const elapsed = Date.now() - startTimeRef.current;
    remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);

    if (progressRef.current) {
      progressRef.current.style.animationPlayState = "paused";
    }
  }, [duration]);

  const resumeTimer = useCallback(() => {
    if (duration <= 0) return;

    if (progressRef.current) {
      progressRef.current.style.animationPlayState = "running";
    }

    startTimer();
  }, [duration, startTimer]);

  useEffect(() => {
    setIsVisible(true);

    if (duration > 0) {
      startTimer();
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsPaused(true);
    pauseTimer();
  }, [pauseTimer]);

  const handleMouseLeave = useCallback(() => {
    setIsPaused(false);
    resumeTimer();
  }, [resumeTimer]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        handleDismiss();
      }
    },
    [handleDismiss]
  );

  return (
    <div
      role={role}
      aria-live="polite"
      aria-atomic="true"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={clsx(
        "relative overflow-hidden flex flex-col w-fit min-w-[250px] max-w-[500px] bg-black text-white rounded-[4px] border border-black-300 transform transition-all duration-300 ease-in-out",
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0",
        className
      )}
      // style={{ borderColor: color }}
    >
      <div className="flex items-center gap-2.5 p-3">
        {Icon && <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color }} aria-hidden="true" />}

        <div className="flex-1 min-w-0">
          <p className="text-bodySm break-words" style={{ color }}>
            {data.message}
          </p>

          {data.action && (
            <button
              onClick={() => {
                // The action closes the snackbar it came from: once acted on, its message is done (2026-10-22).
                data.action?.onClick();
                handleDismiss();
              }}
              data-testid="snackbar-action"
              className="mt-2 text-bodySm underline hover:no-underline transition-all"
              style={{ color }}
              aria-label={data.action.label}
            >
              {data.action.label}
            </button>
          )}
        </div>

        <button aria-label="Dismiss notification" className="flex-shrink-0 p-1 rounded-full hover:bg-black-300 transition-colors" onClick={handleDismiss}>
          <IconClose className="w-4 h-4 text-grey hover:text-white" />
        </button>
      </div>

      {duration > 0 && (
        <div
          ref={progressRef}
          className="absolute bottom-0 left-0 h-[1px] bg-current transition-all"
          style={{
            backgroundColor: color,
            animation: `progress-bar ${duration}ms linear forwards`,
            animationPlayState: isPaused ? "paused" : "running",
          }}
        />
      )}
    </div>
  );
});

Snackbar.displayName = "Snackbar";

export default Snackbar;
